import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../context/UserContext";
import { UserServices, fetchWhatsNewReleases, clearWhatsNewReleasesCache } from "../api/UsersApi";

export default function useWhatsNew() {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const lastSeen = currentUser?.last_seen_release_version ?? null;
  const latestVersion = releases[0]?.versionNumber ?? null;
  const hasUnreadRelease = Boolean(latestVersion) && latestVersion !== lastSeen;

  const loadReleases = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    setError(null);
    fetchWhatsNewReleases()
      .then((result) => {
        const versions = Array.isArray(result?.versions) ? result.versions : [];
        setReleases(versions);
      })
      .catch((err) => {
        setError(err);
        fetchedRef.current = false;
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  const markAsRead = useCallback(async () => {
    if (!latestVersion || latestVersion === lastSeen) return;
    const previousUser = currentUser;
    // Optimistic update: flip the badge immediately.
    setCurrentUser({ ...currentUser, last_seen_release_version: latestVersion });
    try {
      await UserServices.updateLastSeenRelease(latestVersion);
    } catch (err) {
      // Rollback on failure so the badge accurately reflects server state.
      setCurrentUser(previousUser);
    }
  }, [latestVersion, lastSeen, currentUser, setCurrentUser]);

  return {
    releases,
    hasUnreadRelease,
    loading,
    error,
    markAsRead,
    reload: () => {
      fetchedRef.current = false;
      clearWhatsNewReleasesCache();
      loadReleases();
    },
  };
}
